/**
 * In-memory, per-app-session cache for the GitHub data shown in the Settings modal
 * (connection status, repo list, GitHub Projects v2, and codebase scan status).
 *
 * The Settings modal used to re-fetch all of this every time it opened, with each
 * piece of UI updating the moment its own request resolved — visible one-by-one
 * pop-in on every open. Callers should instead await these loaders together (see
 * Settings.tsx) and only render once everything has resolved. Module-level state
 * means the data survives closing/reopening the modal for the lifetime of the page
 * (i.e. once per session) — a full page reload, or resetGithubSessionCache(), is
 * what starts it fresh.
 */
import { getMe } from 'api/auth';
import { getRepos, getProjectsV2, GitHubRepo } from 'api/github';
import { getRepoScanStatus } from 'api/ai';

export type GitHubProjectOption = { id: string; title: string; number: number };

export interface RepoProjectsResult {
    projects: GitHubProjectOption[];
    error: string;
}

export interface RepoScanStatusResult {
    lastScannedAt: string | null;
    nextScanAvailableAt: string | null;
}

interface GithubBaseData {
    githubConnected: boolean;
    repos: GitHubRepo[];
}

let baseData: GithubBaseData | null = null;
let basePromise: Promise<GithubBaseData> | null = null;

const projectsCache = new Map<string, Promise<RepoProjectsResult>>();
const scanStatusCache = new Map<string, Promise<RepoScanStatusResult>>();

async function fetchBase(): Promise<GithubBaseData> {
    const me = await getMe().catch(() => null);
    if (!me?.githubConnected) return { githubConnected: false, repos: [] };
    const repos = await getRepos().catch(() => []);
    return { githubConnected: true, repos: Array.isArray(repos) ? repos : [] };
}

/** Loads GitHub connection status + repo list once per session; later calls reuse the result. */
export function loadGithubBase(): Promise<GithubBaseData> {
    if (baseData) return Promise.resolve(baseData);
    if (!basePromise) {
        basePromise = fetchBase()
            .then((data) => {
                baseData = data;
                return data;
            })
            .catch((e) => {
                basePromise = null;
                throw e;
            });
    }
    return basePromise;
}

async function fetchProjects(repoFullName: string): Promise<RepoProjectsResult> {
    const parts = repoFullName.trim().split('/');
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
        return { projects: [], error: 'Select a repository first.' };
    }
    try {
        const data = await getProjectsV2(undefined, parts[0], parts[1]);
        if (!Array.isArray(data)) throw new Error('Could not load projects');
        return {
            projects: data,
            error:
                data.length === 0
                    ? 'No GitHub Projects v2 found. Make sure your GitHub account has Projects v2 enabled.'
                    : '',
        };
    } catch (e) {
        return { projects: [], error: e instanceof Error ? e.message : 'Unknown error' };
    }
}

/** Loads GitHub Projects v2 for a repo once per session; later calls for the same repo reuse the result. */
export function loadRepoProjects(repoFullName: string): Promise<RepoProjectsResult> {
    let cached = projectsCache.get(repoFullName);
    if (!cached) {
        cached = fetchProjects(repoFullName).catch((e) => {
            projectsCache.delete(repoFullName);
            throw e;
        });
        projectsCache.set(repoFullName, cached);
    }
    return cached;
}

async function fetchScanStatus(repoFullName: string): Promise<RepoScanStatusResult> {
    const status = await getRepoScanStatus(repoFullName);
    if (status.error) return { lastScannedAt: null, nextScanAvailableAt: null };
    return {
        lastScannedAt: status.hasScan && status.scannedAt ? status.scannedAt : null,
        nextScanAvailableAt: status.nextAvailableAt || null,
    };
}

/** Loads codebase-scan status for a repo once per session; later calls for the same repo reuse the result. */
export function loadRepoScanStatus(repoFullName: string): Promise<RepoScanStatusResult> {
    let cached = scanStatusCache.get(repoFullName);
    if (!cached) {
        cached = fetchScanStatus(repoFullName).catch((e) => {
            scanStatusCache.delete(repoFullName);
            throw e;
        });
        scanStatusCache.set(repoFullName, cached);
    }
    return cached;
}

/**
 * Overwrites the cached scan status for a repo — call this right after a successful
 * "Scan Codebase" so a later reopen of Settings reflects it without a round-trip.
 */
export function setRepoScanStatusCache(repoFullName: string, result: RepoScanStatusResult): void {
    scanStatusCache.set(repoFullName, Promise.resolve(result));
}

/** Clears all cached GitHub session data — call after the user disconnects GitHub. */
export function resetGithubSessionCache(): void {
    baseData = null;
    basePromise = null;
    projectsCache.clear();
    scanStatusCache.clear();
}
